import { useAutoneTranslation } from '@autone/translations';
import {
  AutoneTooltip,
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  DataTableIntegerValue,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../../consts';

const COLUMN_ID = 'store_units' as const;

export type StoreUnitsColumnId = typeof COLUMN_ID;

interface StoreUnitsFields {
  store_inventory_units: number;
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<StoreUnitsFields>();

export const createStoreUnitsColumn = <
  T extends StoreUnitsFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.store_inventory_units),
    {
      id: COLUMN_ID,
      size: 175,
      header: function StoreUnitsHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        const tooltipText = useMemo(() => {
          return t('table-column-tooltips.store-units.product-view-table');
        }, [t]);

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="left"
            showSortable
          >
            <DataTableCellContent align={'right'}>
              <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
                {t('table-columns.store-units')}
                <AutoneTooltip text={tooltipText}>
                  <Info className="inline" size={14} />
                </AutoneTooltip>
              </DataTableCellTitle>
            </DataTableCellContent>
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function StoreUnitsCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableIntegerValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
